import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import PromotionFieldsBuilder from './PromotionFieldsBuilder';
import LeadCaptureModal from '../marketplace/LeadCaptureModal';
import promotionsService from '../../services/promotionsService';
import {
  PROMOTION_SURFACES,
  Promotion,
  PromotionField,
  PromotionSurface,
} from '../../types/promotion';

interface PromotionEditDialogProps {
  open: boolean;
  /** Existing promotion to edit, or null to create. */
  initial: Promotion | null;
  onClose: () => void;
  onSaved: (promo: Promotion) => void;
}

const KNOWN_TENANTS = ['quantleap', 'zebu'];

const blankPromotion = (): Promotion => ({
  promo_id: '',
  title: '',
  subtitle: '',
  cta_text: 'Notify me',
  success_text: 'Thanks, our team will reach out shortly.',
  consent_text: 'By submitting, you consent to be contacted about this product.',
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, max_length: 100 },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'phone', label: 'Mobile', type: 'tel', required: true },
  ],
  tenants: ['zebu'],
  surfaces: ['marketplace'],
  start_at: null,
  end_at: null,
  is_active: true,
  auto_popup: true,
});

function toDatetimeLocal(value?: string | null): string {
  if (!value) return '';
  // Convert ISO Z → local "YYYY-MM-DDTHH:MM" string for <input type=datetime-local>
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

const PromotionEditDialog: React.FC<PromotionEditDialogProps> = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [promo, setPromo] = useState<Promotion>(initial || blankPromotion());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPromo(initial ? { ...initial } : blankPromotion());
      setSaving(false);
      setError(null);
    }
  }, [open, initial]);

  const setField = <K extends keyof Promotion>(key: K, value: Promotion[K]) => {
    setPromo((p) => ({ ...p, [key]: value }));
  };

  const toggleArrayValue = <T,>(key: 'tenants' | 'surfaces', value: T) => {
    setPromo((p) => {
      const cur = (p[key] as unknown as T[]) || [];
      const has = cur.includes(value);
      const next = has ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...p, [key]: next as any };
    });
  };

  const validate = (): string | null => {
    if (!promo.promo_id || !/^[A-Za-z0-9_-]+$/.test(promo.promo_id)) {
      return 'promo_id must be a slug (a–z, 0–9, hyphen, underscore)';
    }
    if (!promo.title.trim()) return 'title is required';
    if (!promo.fields.length) return 'add at least one field';
    for (const f of promo.fields) {
      if (!f.name) return 'every field needs a name';
      if (!f.label) return `field "${f.name}" needs a label`;
    }
    if (!promo.tenants.length) return 'pick at least one tenant';
    if (!promo.surfaces.length) return 'pick at least one surface';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    try {
      const saved = isEdit
        ? await promotionsService.updatePromotion(promo.promo_id, promo)
        : await promotionsService.createPromotion(promo);
      onSaved(saved);
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
      setSaving(false);
    }
  };

  // Live preview: build a lightweight Promotion object the modal can render
  const previewPromotion = useMemo<Promotion>(() => ({
    ...promo,
    promo_id: promo.promo_id || 'preview',
  }), [promo]);
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <Modal isOpen={open} onClose={saving ? () => {} : onClose} maxWidth="4xl" ariaLabel="Edit promotion">
      <div className="flex flex-col max-h-[90vh]">
        <div className="relative px-6 pt-6 pb-3 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit promotion' : 'New promotion'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Configure the lead-capture popup. Changes go live for all matching visitors immediately.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Identity */}
          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Identity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">promo_id (slug)</label>
                <Input
                  value={promo.promo_id}
                  onChange={(e) => setField('promo_id', e.target.value.replace(/[^A-Za-z0-9_-]/g, '').toLowerCase())}
                  disabled={isEdit || saving}
                  placeholder="zebu-chennai-launch-2026-04"
                />
              </div>
              <div className="flex items-center gap-4 pt-5">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={promo.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-zebu-600 focus:ring-zebu-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={promo.auto_popup}
                    onChange={(e) => setField('auto_popup', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-zebu-600 focus:ring-zebu-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Auto-popup once per day</span>
                </label>
              </div>
            </div>
          </section>

          {/* Copy */}
          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Copy</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Title</label>
                <Input value={promo.title} onChange={(e) => setField('title', e.target.value)} placeholder="Get early access" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">CTA button text</label>
                <Input value={promo.cta_text} onChange={(e) => setField('cta_text', e.target.value)} placeholder="Notify me" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Subtitle</label>
              <Input value={promo.subtitle ?? ''} onChange={(e) => setField('subtitle', e.target.value)} placeholder="Leave your details and the team will reach out shortly." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Success text</label>
              <Input value={promo.success_text} onChange={(e) => setField('success_text', e.target.value)} placeholder="Thanks, our team will reach out shortly." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Consent disclaimer</label>
              <Input value={promo.consent_text ?? ''} onChange={(e) => setField('consent_text', e.target.value)} placeholder="By submitting, you consent to be contacted." />
            </div>
          </section>

          {/* Fields */}
          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Fields</h3>
            <PromotionFieldsBuilder
              fields={promo.fields}
              onChange={(fields: PromotionField[]) => setField('fields', fields)}
            />
          </section>

          {/* Targeting */}
          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Targeting</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Tenants</label>
              <div className="flex flex-wrap gap-2">
                {KNOWN_TENANTS.map((t) => {
                  const active = promo.tenants.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleArrayValue('tenants', t)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 border-zebu-300 dark:border-zebu-700'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Surfaces</label>
              <div className="flex flex-wrap gap-2">
                {PROMOTION_SURFACES.map((s: PromotionSurface) => {
                  const active = promo.surfaces.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleArrayValue('surfaces', s)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 border-zebu-300 dark:border-zebu-700'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Start (optional)</label>
                <input
                  type="datetime-local"
                  value={toDatetimeLocal(promo.start_at)}
                  onChange={(e) => setField('start_at', fromDatetimeLocal(e.target.value))}
                  className="h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-zebu-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">End (optional)</label>
                <input
                  type="datetime-local"
                  value={toDatetimeLocal(promo.end_at)}
                  onChange={(e) => setField('end_at', fromDatetimeLocal(e.target.value))}
                  className="h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-zebu-500"
                />
              </div>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(true)} disabled={saving}>
              Preview
            </Button>
            {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} isLoading={saving}>
              {isEdit ? 'Save changes' : 'Create promotion'}
            </Button>
          </div>
        </div>
      </div>

      {previewOpen && (
        <LeadCaptureModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          promotion={previewPromotion}
          source="marketplace"
        />
      )}
    </Modal>
  );
};

export default PromotionEditDialog;
